// Sol sidebar bölüm nav — plan §4.4.1 (280px) + §5.3 "7/9" + §6.2 progress.
// Split-window daralması (müşteri talebi): AppShell menüsüyle aynı pattern'le
// daraltılabilir — app-store.sectionNavCollapsed persist, daraltınca 48px ray.
// Finar UX P3 Ö6: ≤1024 px'te daraltılmış açılır; açılınca formu itmez (overlay, 48 px
// yer tutucu kalır), Escape/dış tıklama/bölüm seçimi kapatır.

import { useRef } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import type { QuestionSnapshot, SectionSnapshot } from "@mahir/shared";
import { FORM_FILL_COPY, SECTION_TONE_COLOR, sectionTone } from "../../lib/form-fill-copy";
import { useAppStore } from "../../stores/app-store";
import {
  OVERLAY_PANEL_STYLE,
  useOverlayDismiss,
  usePanelFocus,
} from "../../hooks/use-overlay-dismiss";
import {
  selectSectionProgress,
  useFormFillStore,
  type SaveState,
} from "../../stores/form-fill-store";

interface Props {
  sections: SectionSnapshot[];
  questions: QuestionSnapshot[];
  activeSectionId: string | null;
  onSelect: (sectionId: string) => void;
  saveState: SaveState;
  /**
   * Geçmiş Rapor Aktarımı (Task 15, 2A) — aktif köken çipine göre bölüm başına
   * eşleşen soru sayısı. `null` → çip "Tümü" (rozet çizilmez, nav bugünküyle aynı).
   */
  filterCounts?: Record<string, number> | null;
}

export function SectionNav({
  sections,
  activeSectionId,
  onSelect,
  saveState,
  filterCounts = null,
}: Props): JSX.Element {
  const sorted = [...sections].sort((a, b) => a.order - b.order);
  const collapsed = useAppStore((s) => s.sectionNavCollapsed);
  const toggle = useAppStore((s) => s.toggleSectionNav);
  const overlay = useAppStore((s) => s.narrowFormPanels);
  const panelRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLButtonElement>(null);
  const focus = usePanelFocus(collapsed, panelRef, openerRef);
  const close = (): void => {
    focus.markClose();
    toggle();
  };
  useOverlayDismiss(panelRef, overlay && !collapsed, toggle, close);

  if (collapsed) {
    return (
      <nav
        style={{
          width: 48,
          flexShrink: 0,
          background: "var(--color-surface)",
          borderRight: "1px solid var(--color-border)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 12,
          gap: 12,
        }}
      >
        <button
          ref={openerRef}
          onClick={() => {
            focus.markOpen();
            toggle();
          }}
          aria-label="Bölüm panelini aç"
          title="Bölüm panelini aç"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--color-text-3)",
            padding: 6,
          }}
        >
          <PanelLeftOpen size={18} />
        </button>
        <span
          style={{
            writingMode: "vertical-rl",
            fontFamily: "var(--font-mono)",
            fontSize: "0.68rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
          }}
        >
          Bölümler
        </span>
      </nav>
    );
  }

  const panel = (
    <nav
      ref={panelRef}
      aria-label="Bölümler"
      style={{
        width: 280,
        flexShrink: 0,
        background: "var(--color-surface)",
        borderRight: "1px solid var(--color-border)",
        height: "100%",
        overflowY: "auto",
        padding: 16,
        ...(overlay ? { ...OVERLAY_PANEL_STYLE, left: 0 } : {}),
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.08em",
            color: "var(--color-text-3)",
            textTransform: "uppercase",
          }}
        >
          Bölümler
        </span>
        <button
          onClick={close}
          aria-label="Bölüm panelini daralt"
          title="Bölüm panelini daralt"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--color-text-3)",
            padding: 2,
          }}
        >
          <PanelLeftClose size={16} />
        </button>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {sorted.map((s) => (
          <SectionItem
            key={s.id}
            section={s}
            isActive={s.id === activeSectionId}
            onSelect={() => {
              onSelect(s.id);
              if (overlay) close();
            }}
            showActiveIndicator={s.id === activeSectionId && saveState !== "idle"}
            saveState={saveState}
            filterCount={filterCounts ? (filterCounts[s.id] ?? 0) : null}
          />
        ))}
      </ul>
    </nav>
  );
  if (!overlay) return panel;
  // Overlay: akışta 48 px (daraltılmış rayla aynı) kalır, panel formun üstüne açılır.
  return <div style={{ width: 48, flexShrink: 0, position: "relative", zIndex: 20 }}>{panel}</div>;
}

function SectionItem({
  section,
  isActive,
  onSelect,
  showActiveIndicator,
  saveState,
  filterCount,
}: {
  section: SectionSnapshot;
  isActive: boolean;
  onSelect: () => void;
  showActiveIndicator: boolean;
  saveState: SaveState;
  filterCount: number | null;
}): JSX.Element {
  const progress = useFormFillStore(useShallow((s) => selectSectionProgress(s, section.id)));
  const pct = progress.total === 0 ? 0 : Math.round((progress.answered / progress.total) * 100);
  const allRequiredDone =
    progress.requiredTotal > 0 && progress.requiredAnswered === progress.requiredTotal;
  // Renk kodu (§6.2, Ö3): yeşil (tam) / amber (kısmen) / gri (boş). Kısmi bölüm hata
  // kırmızısı DEĞİL — kırmızı yalnız gerçek eksik/kaydedilemeyen alan içindir.
  const statusColor = SECTION_TONE_COLOR[sectionTone(progress, { isActive })];
  const C = FORM_FILL_COPY.sectionCounter;

  return (
    <li>
      <button
        onClick={onSelect}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "10px 12px",
          borderRadius: 8,
          marginBottom: 4,
          background: isActive ? "var(--color-bg)" : "transparent",
          border: "1px solid",
          borderColor: isActive ? "var(--color-primary)" : "transparent",
          cursor: "pointer",
          fontFamily: "var(--font-sans)",
          display: "block",
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.7rem",
              color: "var(--color-text-3)",
            }}
          >
            {section.code}
          </div>
          {/* 2A — aktif köken çipinde bu bölümde kaç soru eşleşiyor. Nötr pill;
              0 olanlar da görünür (bölüm listesi filtreyle kısalmaz). */}
          {filterCount !== null && (
            <span
              title={`Aktarım filtresine uyan soru: ${filterCount.toString()}`}
              style={{
                minWidth: 18,
                padding: "0 5px",
                borderRadius: 999,
                fontFamily: "var(--font-mono)",
                fontSize: "0.65rem",
                fontWeight: 600,
                color: "var(--color-text-2)",
                background: "var(--color-bg)",
                border: "1px solid var(--color-border)",
                opacity: filterCount === 0 ? 0.6 : 1,
              }}
            >
              {filterCount}
            </span>
          )}
          {showActiveIndicator && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background:
                  saveState === "saving" || saveState === "syncing"
                    ? "var(--color-warning-text)"
                    : saveState === "offline" ||
                        saveState === "conflict" ||
                        saveState === "invalid" ||
                        saveState === "failed"
                      ? "var(--color-error)"
                      : saveState === "saved"
                        ? "var(--color-success)"
                        : "var(--color-text-3)",
                display: "inline-block",
              }}
            />
          )}
        </div>
        <div
          style={{
            color: isActive ? "var(--color-primary)" : "var(--color-text)",
            fontSize: "0.88rem",
            fontWeight: isActive ? 600 : 500,
          }}
        >
          {section.label}
        </div>
        <div
          className="mt-1 flex items-center gap-2"
          style={{ fontSize: "0.7rem", fontFamily: "var(--font-mono)" }}
        >
          <span className="sr-only">
            {C.summary(
              progress.answered,
              progress.total,
              progress.requiredAnswered,
              progress.requiredTotal,
            )}
          </span>
          <span aria-hidden="true" style={{ color: statusColor, whiteSpace: "nowrap" }}>
            {C.answered(progress.answered, progress.total)}
          </span>
          <span
            aria-hidden="true"
            style={{
              flex: 1,
              minWidth: 24,
              height: 4,
              background: "var(--color-border)",
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "block",
                height: "100%",
                width: `${pct}%`,
                background: statusColor,
                transition: "width 120ms ease-out",
              }}
            />
          </span>
          {progress.requiredTotal > 0 && (
            <span
              aria-hidden="true"
              style={{
                color: allRequiredDone ? "var(--color-success)" : "var(--color-text-3)",
                fontSize: "0.65rem",
                whiteSpace: "nowrap",
              }}
            >
              {C.required(progress.requiredAnswered, progress.requiredTotal)}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}
